import { SceneWithUserNames } from '../../../../types/scene.types';

// 场号格式类型（保留接口兼容现有调用）
export interface SceneCodeParts {
  mainNumber: number;
  type: 'number' | 'number-quote' | 'dash-number' | 'dash-letter' | 'dash-empty' | 'number-letter' | 'invalid';
  suffix?: string;
  secondNumber?: number;
  quoteCount?: number;
  extraSuffix?: string;
  originalString?: string;
}

type TokenType = 'symbol' | 'number' | 'english' | 'chinese';

interface SceneToken {
  type: TokenType;
  value: string;
}

const SYMBOL_PRIORITY: Record<string, number> = {
  "'": 0,
  ' ': 1,
  '#': 2,
  '/': 3,
  '-': 4,
  '_': 5,
  '&': 6,
  '+': 7,
  '.': 8,
  '@': 9,
};

const TYPE_PRIORITY: Record<TokenType, number> = {
  symbol: 0,
  number: 1,
  english: 2,
  chinese: 3,
};

const getTokenType = (char: string): TokenType => {
  if (/\d/.test(char)) return 'number';
  if (/[A-Za-z]/.test(char)) return 'english';
  if (/[\u4e00-\u9fff]/.test(char)) return 'chinese';
  return 'symbol';
};

const tokenizeSceneCode = (input: string): SceneToken[] => {
  const text = input.trim();
  if (!text) return [];

  const tokens: SceneToken[] = [];

  for (const char of text) {
    const currentType = getTokenType(char);
    const last = tokens[tokens.length - 1];

    if (!last || last.type !== currentType) {
      tokens.push({ type: currentType, value: char });
      continue;
    }

    // 连续数字必须整体比较；英文和汉字也按连续片段比较
    last.value += char;
  }

  return tokens;
};

const compareSymbolChar = (a: string, b: string): number => {
  if (a === b) return 0;

  const rankA = Object.prototype.hasOwnProperty.call(SYMBOL_PRIORITY, a) ? SYMBOL_PRIORITY[a] : Number.MAX_SAFE_INTEGER;
  const rankB = Object.prototype.hasOwnProperty.call(SYMBOL_PRIORITY, b) ? SYMBOL_PRIORITY[b] : Number.MAX_SAFE_INTEGER;

  if (rankA !== rankB) return rankA - rankB;

  return a.localeCompare(b);
};

const compareSymbolToken = (a: string, b: string): number => {
  const minLength = Math.min(a.length, b.length);

  for (let i = 0; i < minLength; i += 1) {
    const diff = compareSymbolChar(a[i], b[i]);
    if (diff !== 0) return diff;
  }

  return a.length - b.length;
};

const normalizeNumeric = (value: string): string => {
  const stripped = value.replace(/^0+/, '');
  return stripped === '' ? '0' : stripped;
};

const compareNumberToken = (a: string, b: string): number => {
  const normalizedA = normalizeNumeric(a);
  const normalizedB = normalizeNumeric(b);

  if (normalizedA.length !== normalizedB.length) {
    return normalizedA.length - normalizedB.length;
  }

  if (normalizedA !== normalizedB) {
    return normalizedA.localeCompare(normalizedB);
  }

  // 数值相同（例如 2 和 02），较短原串优先
  if (a.length !== b.length) {
    return a.length - b.length;
  }

  return a.localeCompare(b);
};

const compareEnglishToken = (a: string, b: string): number => {
  const minLength = Math.min(a.length, b.length);

  for (let i = 0; i < minLength; i += 1) {
    const charA = a[i];
    const charB = b[i];

    const lowerA = charA.toLowerCase();
    const lowerB = charB.toLowerCase();

    if (lowerA !== lowerB) {
      return lowerA.localeCompare(lowerB);
    }

    if (charA !== charB) {
      const isUpperA = charA >= 'A' && charA <= 'Z';
      const isUpperB = charB >= 'A' && charB <= 'Z';

      if (isUpperA !== isUpperB) {
        // 同字母时，大写优先于小写
        return isUpperA ? -1 : 1;
      }
    }
  }

  return a.length - b.length;
};

const compareChineseToken = (a: string, b: string): number => {
  return a.localeCompare(b, 'zh-Hans-u-co-pinyin', {
    sensitivity: 'variant',
    numeric: true,
  });
};

const compareToken = (a: SceneToken, b: SceneToken): number => {
  if (a.type !== b.type) {
    return TYPE_PRIORITY[a.type] - TYPE_PRIORITY[b.type];
  }

  if (a.type === 'symbol') return compareSymbolToken(a.value, b.value);
  if (a.type === 'number') return compareNumberToken(a.value, b.value);
  if (a.type === 'english') return compareEnglishToken(a.value, b.value);
  return compareChineseToken(a.value, b.value);
};

const compareSceneCodeText = (a: string, b: string): number => {
  const tokensA = tokenizeSceneCode(a);
  const tokensB = tokenizeSceneCode(b);

  const minLength = Math.min(tokensA.length, tokensB.length);

  for (let i = 0; i < minLength; i += 1) {
    const diff = compareToken(tokensA[i], tokensB[i]);
    if (diff !== 0) return diff;
  }

  return tokensA.length - tokensB.length;
};

// 解析场号（简化版，保留导出兼容）
export const parseSceneCode = (sceneCode: string | null | undefined): SceneCodeParts | null => {
  if (!sceneCode || sceneCode.trim() === '') {
    return null;
  }

  const trimmed = sceneCode.trim();
  const leadingNumberMatch = trimmed.match(/^(\d+)/);

  if (leadingNumberMatch) {
    const mainNumber = Number.parseInt(leadingNumberMatch[1], 10);
    const extraSuffix = trimmed.slice(leadingNumberMatch[1].length) || undefined;

    return {
      mainNumber,
      type: 'number',
      extraSuffix,
      originalString: sceneCode,
    };
  }

  return {
    mainNumber: Number.MAX_SAFE_INTEGER,
    type: 'invalid',
    originalString: sceneCode,
  };
};

// 场号排序比较函数（按统一新规则）
export const compareSceneCodes = (
  a: SceneWithUserNames,
  b: SceneWithUserNames,
  parseCache?: Map<string, SceneCodeParts | null>,
): number => {
  const codeA = a.sceneCode?.trim() || '';
  const codeB = b.sceneCode?.trim() || '';

  // 空值排最后
  if (!codeA && !codeB) return 0;
  if (!codeA) return 1;
  if (!codeB) return -1;

  // 保留参数兼容，不再使用旧解析缓存规则
  void parseCache;

  return compareSceneCodeText(codeA, codeB);
};

// 中文拼音排序比较函数
export const compareChineseText = (a: string, b: string): number => {
  const isEmptyA = !a || a.trim() === '' || a === '选择或创建场景';
  const isEmptyB = !b || b.trim() === '' || b === '选择或创建场景';

  if (isEmptyA && isEmptyB) return 0;
  if (isEmptyA) return 1;
  if (isEmptyB) return -1;

  return a.localeCompare(b, 'zh-Hans-u-co-pinyin', {
    numeric: true,
    sensitivity: 'base',
  });
};

// 通用排序函数（带解析结果缓存优化，内部使用策略模式）
// 为了保持向后兼容，保留此函数作为主要入口
// 注意：实际实现在 SceneSortStrategy.ts 中，这里重新导出以避免循环依赖
// 使用动态导入来避免循环依赖问题
let sortScenesImpl: ((scenes: SceneWithUserNames[], sortKey: string, direction: 'asc' | 'desc') => SceneWithUserNames[]) | null = null;

export const sortScenes = (scenes: SceneWithUserNames[], sortKey: string, direction: 'asc' | 'desc'): SceneWithUserNames[] => {
  if (!sortScenesImpl) {
    const { sortScenesWithStrategy } = require('./SceneSortStrategy');
    sortScenesImpl = sortScenesWithStrategy;
  }

  return sortScenesImpl!(scenes, sortKey, direction);
};

// 场号格式验证（已取消限制，始终返回 true）
export const validateSceneCode = (_sceneCode: string): boolean => {
  return true;
};
